import Baru from '@atoms/Baru';
import RemoteImage from '@atoms/RemoteImage';
import TextCurrency from '@atoms/TextCurrency';
import { Text, Button } from '@components/primitives';
import { MaterialIcons } from '@expo/vector-icons';
import ModalAccounts from '@molecules/ModalAccounts';
import appStyles from '@root/appStyles';
import colors from '@theme/colors';
import { scale } from '@utils/normalize';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { View, SafeAreaView, TouchableOpacity, Pressable } from 'react-native';
import { logAccountClick, logAccountSelected } from './analyticsAccountSelector';
import styles from './styles';

type SetShowAccountSelector = (show: boolean) => void;
type SelectedAccount = {
  icon: string;
  color: string;
  displayName: string;
  balance?: number;
  accountTypeId?: number;
  accountId?: number;
  amplitudeAccountName?: string;
};
type MeAmplitudeContext = {
  key: string;
  value: string | number;
  event: string[];
};
// Define the types for your props
interface OpenPeriod {
  start: string;
  end: string;
  payDateWithdrawal: boolean;
}

interface Slider {
  enabled: boolean;
  incrementBy: number;
  minValue: number;
  maxValue: number;
}

interface WithdrawalButton {
  enabled: boolean;
}

interface WithdrawalAccount {
  id: number;
  accountTypeId: number;
  balance: number;
  displayBalance: boolean;
  icon: string;
  color: string;
  displayName: string;
  amplitudeAccountName: string;
  isDynamic: boolean;
  displayOrder: number;
  enableAccountCard: boolean;
  screenName: string | null;
  hasSlider: boolean;
  sliderStep: number;
  buttonText: string;
  defaultSliderPercentage: number;
  minTxnValue: number;
  isWithdrawalEligible: boolean;
  isBillPaymentEligible: boolean;
  status: string;
  openPeriod: OpenPeriod;
  slider: Slider;
  withdrawalButton: WithdrawalButton;
  defaultWithdrawalAmount: number;
  canShowNew: boolean;
}

interface AccountSelectorProps {
  selectedAccount?: SelectedAccount;
  withdrawlAccounts?: WithdrawalAccount[];
  setSelectedAccount?: (accountDetails: WithdrawalAccount) => void;
  homePage?: boolean;
  txnPage?: boolean;
  source?: string;
  meAmplitudeContext?: MeAmplitudeContext[];
  excludeNewTag?: boolean;
}

function openAccountSelector(
  setShowAccountSelector: SetShowAccountSelector,
  selectedAccount: SelectedAccount,
  source: string,
  meAmplitudeContext: MeAmplitudeContext[],
) {
  return () => {
    setShowAccountSelector(true);
    logAccountClick(source, selectedAccount, meAmplitudeContext);
  };
}

function getAccountSelector(
  selectedAccount: SelectedAccount,
  setShowAccountSelector: SetShowAccountSelector,
  homePage: boolean,
  txnPage: boolean,
  t: (key: string) => string,
  source: string,
  showBaruTag: boolean,
  meAmplitudeContext: MeAmplitudeContext[],
): React.JSX.Element {
  const { icon, color: accountBGColor, displayName, balance, accountTypeId } = selectedAccount;

  if (txnPage) {
    return (
      <Pressable
        testID="btnAccountSelector"
        sentry-label="btnAccountSelector"
        onPress={openAccountSelector(setShowAccountSelector, selectedAccount, source, meAmplitudeContext)}
        style={styles.container}>
        <Text variant="md-bold">
          {t('accountSelectorText.basedOn')} {displayName}
        </Text>
        <View style={styles.iconContainer} />
        <MaterialIcons name="keyboard-arrow-down" style={styles.arrowIcon} size={scale(24)} />
      </Pressable>
    );
  }

  if (homePage) {
    const customStyles = {
      borderRadius: 12,
    };
    return (
      <>
        <Baru showBaru={showBaruTag} animate customStyles={styles.homePageBaru} />
        <Button
          size="md"
          style={[styles.accountSelectorContainer, { backgroundColor: accountBGColor, ...customStyles }]}
          testID="btnAccountSelector"
          sentry-label="btnAccountSelector"
          _text={{ color: colors.neutral.charcoal, variant: 'sm-bold' }}
          android_ripple={{ color: colors.disabled.smoke }}
          leftIcon={<RemoteImage uri={icon} width={15} height={15} />}
          rightIcon={<MaterialIcons name="keyboard-arrow-down" style={styles.arrowIcon} size={scale(24)} />}
          onPress={openAccountSelector(setShowAccountSelector, selectedAccount, source, meAmplitudeContext)}>
          {displayName}
        </Button>
      </>
    );
  }

  return (
    <>
      <Baru showBaru={showBaruTag} animate customStyles={styles.baruStyle} />
      <TouchableOpacity
        testID="btnAccountSelector"
        sentry-label="btnAccountSelector"
        onPress={openAccountSelector(setShowAccountSelector, selectedAccount, source, meAmplitudeContext)}
        style={styles.toStyles(accountBGColor)}>
        <RemoteImage uri={icon} width={15} height={15} />
        <Text flex={1} color={colors.neutral.charcoal} variant="sm-bold" style={appStyles.marginLeft(4.5)}>
          {displayName}
        </Text>
        <TextCurrency
          amountCurrency={balance}
          showCurrencySymbol={accountTypeId !== 1}
          variant="sm-bold"
          style={appStyles.marginRight(8)}
        />
        <MaterialIcons
          name="keyboard-arrow-down"
          size={scale(24)}
          style={[styles.arrowIcon, { marginRight: scale(8) }]}
        />
      </TouchableOpacity>
    </>
  );
}

const AccountSelector: React.FC<AccountSelectorProps> = ({
  selectedAccount,
  withdrawlAccounts,
  setSelectedAccount,
  homePage,
  txnPage,
  source,
  meAmplitudeContext,
  excludeNewTag,
}) => {
  const [showAccountSelector, setShowAccountSelector] = useState<boolean>(false);
  const { t } = useTranslation('common');

  const selectedAccountHandler = (accountDetails: WithdrawalAccount): void => {
    logAccountSelected(source, accountDetails, meAmplitudeContext);
    setSelectedAccount(accountDetails);
  };

  let showBaruTag = false;

  if (!excludeNewTag) showBaruTag = withdrawlAccounts.some(e => e.canShowNew === true);

  return (
    <SafeAreaView>
      <View style={styles.accountSelectorRow}>
        {getAccountSelector(
          selectedAccount,
          setShowAccountSelector,
          homePage,
          txnPage,
          t,
          source,
          showBaruTag,
          meAmplitudeContext,
        )}
      </View>
      <ModalAccounts
        showAccountSelector={showAccountSelector}
        setShowAccountSelector={setShowAccountSelector}
        withdrawlAccounts={withdrawlAccounts}
        setSelectedAccount={selectedAccountHandler}
        source={source}
        selectedAccount={selectedAccount}
        excludeNewTag={excludeNewTag}
      />
    </SafeAreaView>
  );
};

AccountSelector.defaultProps = {
  selectedAccount: {
    icon: '',
    color: '',
    displayName: '',
  },
  withdrawlAccounts: [{}],
  setSelectedAccount: () => {},
  homePage: true,
  txnPage: false,
  meAmplitudeContext: [],
  excludeNewTag: false,
};

export default AccountSelector;
